import React, { useState, useEffect } from 'react'
import { Card } from '@/components/ui'
import { Trophy, Medal, Award, TrendingUp } from 'lucide-react'
import { loadTopRankings } from '@/lib/rankings'
import { StudioRankings } from '@/types'
import { formatCurrency } from '@/lib/utils'

interface TopRankingsProps {
  rankingType?: 'global' | 'regional' | 'size_group'
  limit?: number
  month?: string
  className?: string
}

export function TopRankings({ 
  rankingType = 'global', 
  limit = 10, 
  month,
  className = '' 
}: TopRankingsProps) {
  const [rankings, setRankings] = useState<StudioRankings[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadData()
  }, [rankingType, limit, month])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const data = await loadTopRankings(month, rankingType, limit)
      setRankings(data)
    } catch (err) {
      setError('Ошибка при загрузке рейтингов')
    } finally {
      setLoading(false)
    }
  }

  const getPositionIcon = (position: number) => {
    if (position === 1) return <Trophy className="w-5 h-5 text-yellow-500" />
    if (position === 2) return <Medal className="w-5 h-5 text-gray-400" />
    if (position === 3) return <Medal className="w-5 h-5 text-orange-600" />
    return <Award className="w-5 h-5 text-gray-500" />
  }

  const getPositionStyle = (position: number) => {
    if (position <= 3) return 'bg-success/10 border-success/20'
    return 'bg-card border-border'
  }

  const getRankValue = (item: StudioRankings) => {
    switch (rankingType) {
      case 'global':
        return item.global_rank
      case 'regional':
        return item.regional_rank
      case 'size_group':
        return item.size_group_rank
      default:
        return item.global_rank
    }
  }

  const getTitle = () => {
    switch (rankingType) {
      case 'global':
        return 'Топ студий (общий рейтинг)'
      case 'regional':
        return 'Топ студий (по регионам)'
      case 'size_group':
        return 'Топ студий (по группам городов)'
      default:
        return 'Топ студий'
    }
  }

  if (loading) {
    return (
      <Card className={className}>
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            {getTitle()}
          </h3>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 animate-pulse">
                <div className="w-8 h-8 bg-muted rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="w-24 h-4 bg-muted rounded"></div>
                  <div className="w-16 h-3 bg-muted rounded"></div>
                </div>
                <div className="w-20 h-4 bg-muted rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={className}>
        <div className="text-center py-8">
          <p className="text-error">{error}</p>
          <button 
            onClick={loadData}
            className="mt-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
          >
            Повторить
          </button>
        </div>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          {getTitle()}
        </h3>
        
        {rankings.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>Нет данных за выбранный период</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rankings.map((studio) => {
              const position = getRankValue(studio)
              return (
                <div
                  key={studio.studio_id}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${getPositionStyle(position)}`}
                >
                  <div className="flex items-center gap-2 min-w-[60px]">
                    {getPositionIcon(position)}
                    <span className="font-semibold text-sm">#{position}</span>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                      {studio.studio_number}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {studio.city_name}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="font-bold text-sm">
                      {formatCurrency(studio.total_revenue)}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        
        <div className="pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground text-center">
            Данные за {month || 'текущий месяц'} • По выручке
          </p>
        </div>
      </div>
    </Card>
  )
}